import { useState, useEffect, useMemo, useRef } from 'react'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faGripVertical,
    faAngleUp,
    faAngleDown,
    faAnglesUp,
    faAnglesDown,
    faMagnifyingGlass,
} from '@fortawesome/free-solid-svg-icons'
import styles from './SortableFieldList.module.css'

/**
 * Reusable, keyboard-accessible list for re-ranking fields.
 *
 * Improvements over a plain drag-and-drop list, aimed at large lists:
 *  - Explicit Save (edits stay local until saved) with a dirty/saving/saved/error status
 *  - Per-row move up / down / to-top / to-bottom buttons
 *  - Direct "rank #" entry so far moves need no dragging
 *  - Search/filter to find a field in a long list
 *  - Compact rows with a dedicated drag handle (the row stays scrollable)
 *
 * Props:
 *  - items      : committed list from the server (source of truth)
 *  - getId      : (item) => unique id            (default: item.id)
 *  - getLabel   : (item) => display label        (default: item.cssFieldName)
 *  - onSave     : async (orderedItems) => void   (may throw; errors are surfaced)
 *  - rankKey    : property to write index+1 into (default: 'overrideRank')
 *  - emptyText  : shown when there are no fields
 *  - onChange   : (orderedItems) => void   fired whenever the working order changes
 *                 (lets a parent Save/Continue outside this component's own button)
 *  - showSaveButton : show the built-in Save/Reset buttons (default true). Set false
 *                 when the parent owns saving; the status text is still shown.
 *  - renderLabel   : (item, index) => node   custom content for the label cell
 *  - renderActions : (item, index) => node   extra controls after the move buttons
 */
const SortableFieldList = ({
    items = [],
    getId = (item) => item.id,
    getLabel = (item) => item.cssFieldName,
    onSave,
    rankKey = 'overrideRank',
    emptyText = 'No fields to order.',
    onChange,
    showSaveButton = true,
    renderLabel,
    renderActions,
}) => {
    const applyRanks = (arr) => arr.map((item, i) => ({ ...item, [rankKey]: i + 1 }))

    const [draft, setDraft] = useState(() => applyRanks(items))
    const [filter, setFilter] = useState('')
    const [status, setStatus] = useState('idle') // idle | saving | saved | error
    const [errorMsg, setErrorMsg] = useState('')

    // Reseed local draft whenever the committed list changes (new fetch / event switch).
    useEffect(() => {
        setDraft(applyRanks(items))
        setStatus('idle')
        setErrorMsg('')
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items])

    // Lift the current order to a parent that owns saving (ExtraBadge wizard, etc.).
    const onChangeRef = useRef(onChange)
    onChangeRef.current = onChange
    useEffect(() => {
        if (onChangeRef.current) onChangeRef.current(draft)
    }, [draft])

    const dirty = useMemo(() => {
        if (draft.length !== items.length) return true
        for (let i = 0; i < draft.length; i++) {
            if (String(getId(draft[i])) !== String(getId(items[i]))) return true
        }
        return false
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draft, items])

    const move = (from, to) => {
        if (from === to || from < 0 || to < 0 || from >= draft.length || to >= draft.length) return
        const next = Array.from(draft)
        const [moved] = next.splice(from, 1)
        next.splice(to, 0, moved)
        setDraft(applyRanks(next))
        if (status === 'saved') setStatus('idle')
    }

    const moveToTop = (i) => move(i, 0)
    const moveToBottom = (i) => move(i, draft.length - 1)
    const moveUp = (i) => move(i, i - 1)
    const moveDown = (i) => move(i, i + 1)

    const commitRank = (fromIndex, rawValue, inputEl) => {
        const parsed = parseInt(rawValue, 10)
        if (Number.isNaN(parsed) || parsed === fromIndex + 1) {
            if (inputEl) inputEl.value = String(fromIndex + 1) // revert stray text
            return
        }
        const to = Math.min(Math.max(parsed, 1), draft.length) - 1
        move(fromIndex, to)
    }

    const handleDragEnd = (result) => {
        if (!result.destination) return
        move(result.source.index, result.destination.index)
    }

    const handleSave = async () => {
        if (!dirty || status === 'saving') return
        setStatus('saving')
        setErrorMsg('')
        try {
            await onSave(draft)
            setStatus('saved')
        } catch (err) {
            setErrorMsg((err && err.message) || 'Failed to save ordering. Please try again.')
            setStatus('error')
        }
    }

    const handleReset = () => {
        setDraft(applyRanks(items))
        setStatus('idle')
        setErrorMsg('')
    }

    const isFiltering = filter.trim().length > 0
    const needle = filter.trim().toLowerCase()
    const matches = (item) => String(getLabel(item) ?? '').toLowerCase().includes(needle)

    // Rows carry their absolute index in the draft so filtered rank edits stay correct.
    const rows = draft
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => !isFiltering || matches(item))

    const statusLabel = () => {
        if (status === 'saving') return { text: 'Saving…', cls: styles.statusSaving }
        if (status === 'error') return { text: errorMsg, cls: styles.statusError }
        if (dirty) return { text: 'Unsaved changes', cls: styles.statusDirty }
        if (status === 'saved') return { text: 'All changes saved', cls: styles.statusSaved }
        return { text: 'Saved', cls: styles.statusIdle }
    }
    const s = statusLabel()

    const renderRowInner = (item, index, dragHandleProps) => (
        <div className={styles.row}>
            <span
                className={`${styles.grip} ${isFiltering ? styles.gripDisabled : ''}`}
                title={isFiltering ? 'Clear the search to drag-reorder' : 'Drag to reorder'}
                {...(dragHandleProps || {})}
            >
                <FontAwesomeIcon icon={faGripVertical} />
            </span>

            <input
                type="number"
                min={1}
                max={draft.length}
                className={styles.rankInput}
                aria-label={`Rank for ${getLabel(item)}`}
                title="Type a rank and press Enter to move this field"
                key={`${getId(item)}-${index + 1}`}
                defaultValue={index + 1}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur()
                }}
                onBlur={(e) => commitRank(index, e.currentTarget.value, e.currentTarget)}
            />

            <div className={styles.labelWrap}>
                {renderLabel
                    ? renderLabel(item, index)
                    : <span className={styles.label} title={getLabel(item)}>{getLabel(item)}</span>}
            </div>

            <span className={styles.rowButtons}>
                <button type="button" title="Move to top" disabled={isFiltering || index === 0}
                    onClick={() => moveToTop(index)}>
                    <FontAwesomeIcon icon={faAnglesUp} />
                </button>
                <button type="button" title="Move up" disabled={isFiltering || index === 0}
                    onClick={() => moveUp(index)}>
                    <FontAwesomeIcon icon={faAngleUp} />
                </button>
                <button type="button" title="Move down" disabled={isFiltering || index === draft.length - 1}
                    onClick={() => moveDown(index)}>
                    <FontAwesomeIcon icon={faAngleDown} />
                </button>
                <button type="button" title="Move to bottom" disabled={isFiltering || index === draft.length - 1}
                    onClick={() => moveToBottom(index)}>
                    <FontAwesomeIcon icon={faAnglesDown} />
                </button>
                {renderActions && renderActions(item, index)}
            </span>
        </div>
    )

    return (
        <div className={styles.container}>
            <div className={styles.toolbar}>
                <div className={styles.searchWrap}>
                    <FontAwesomeIcon icon={faMagnifyingGlass} className={styles.searchIcon} />
                    <input
                        type="text"
                        className={styles.search}
                        placeholder="Search fields…"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                    />
                    {isFiltering && (
                        <button type="button" className={styles.clearSearch} onClick={() => setFilter('')}>
                            Clear
                        </button>
                    )}
                </div>

                <div className={styles.actions}>
                    <span className={`${styles.status} ${s.cls}`}>{s.text}</span>
                    {showSaveButton && (
                        <>
                            <button type="button" className={styles.resetButton}
                                onClick={handleReset} disabled={!dirty || status === 'saving'}>
                                Reset
                            </button>
                            <button type="button" className={styles.saveButton}
                                onClick={handleSave} disabled={!dirty || status === 'saving'}>
                                {status === 'saving' ? 'Saving…' : 'Save order'}
                            </button>
                        </>
                    )}
                </div>
            </div>

            {isFiltering && (
                <div className={styles.filterNote}>
                    Showing {rows.length} of {draft.length}. Drag &amp; move buttons are disabled while searching — use the rank box to reposition, or clear the search to drag.
                </div>
            )}

            {draft.length === 0 ? (
                <div className={styles.empty}>{emptyText}</div>
            ) : isFiltering ? (
                <ul className={styles.list}>
                    {rows.map(({ item, index }) => (
                        <li key={String(getId(item))} className={styles.listItem}>
                            {renderRowInner(item, index, null)}
                        </li>
                    ))}
                </ul>
            ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="SortableFieldList">
                        {(provided) => (
                            <ul className={styles.list} {...provided.droppableProps} ref={provided.innerRef}>
                                {rows.map(({ item, index }) => (
                                    <Draggable key={String(getId(item))} draggableId={String(getId(item))} index={index}>
                                        {(dp, snapshot) => (
                                            <li
                                                className={`${styles.listItem} ${snapshot.isDragging ? styles.dragging : ''}`}
                                                ref={dp.innerRef}
                                                {...dp.draggableProps}
                                            >
                                                {renderRowInner(item, index, dp.dragHandleProps)}
                                            </li>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </ul>
                        )}
                    </Droppable>
                </DragDropContext>
            )}
        </div>
    )
}

export default SortableFieldList
